import { useState, useEffect } from 'react'
import { AddButton } from 'components/common/AddButton'
import { CreateEstimatingTemplateModal } from './CreateEstimatingTemplateModal'
import { useLoading } from 'hooks'
import { makeGetRequest, makePostRequest, makeDeleteRequest, makePutRequest } from 'utils/api'
import { List } from './List'
import { useDispatch } from 'react-redux'
import { PUSH_NOTIFICATION } from 'actions/actionTypes'
import { withSweetAlert } from 'hocs'
import { fromItems } from 'components/common/DealEstimatingCosts/serializers'

const EstimateTemplates = () => {
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false)
  const [templates, setTemplates] = useState([])
  const reduxDispatch = useDispatch()
  const [editedTemplate, setEditedTemplate] = useState(null)

  const [, withLoadingLocks] = useLoading()

  useEffect(() => {
    ;(async () => {
      const response = await withLoadingLocks(makeGetRequest('/v1/deal-estimating/template'))
      setTemplates(response)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const openEditModal = async template => {
    try {
      const response = await withLoadingLocks(
        makeGetRequest(`/v1/deal-estimating/template/${template.id}`)
      )
      setEditedTemplate(response)
      setIsCreateModalVisible(true)
    } catch (e) {
      setIsCreateModalVisible(true)
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'There was a problem fetching template',
          level: 'error'
        }
      })
    }
  }

  const deleteTemplate = id => {
    const sweetAlertFactory = withSweetAlert(
      <div>
        <p style={{ color: 'red' }}>This action can&#39;t be reverted</p>
      </div>
    )
    sweetAlertFactory(
      {
        title: 'Are you sure you want to delete this template?',
        buttons: ['No', 'Yes'],
        icon: 'warning'
      },
      async () => {
        try {
          await makeDeleteRequest(`/v1/deal-estimating/template/${id}`)
          const array = [...templates]
          const index = templates.findIndex(u => u.id === id)
          if (~index) {
            array.splice(index, 1)
            setTemplates(array)
            reduxDispatch({
              type: PUSH_NOTIFICATION,
              payload: {
                title: 'Template deleted successfuly',
                level: 'success'
              }
            })
          }
        } catch (e) {
          reduxDispatch({
            type: PUSH_NOTIFICATION,
            payload: {
              title: 'There was a problem deleting template',
              level: 'error'
            }
          })
        }
      },
      { cancelConfirmation: false }
    )
  }

  const cloneTemplate = async id => {
    try {
      const template = await withLoadingLocks(
        makePostRequest(`/v1/deal-estimating/template/${id}/clone`)
      )
      setTemplates([...templates, template])

      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'Template cloned successfully',
          level: 'success'
        }
      })
    } catch (e) {
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'There was a problem cloning template',
          level: 'error'
        }
      })
    }
  }

  const clearModal = () => {
    setIsCreateModalVisible(false)
    setEditedTemplate(null)
  }

  const saveTemplate = async (name, visibility, template) => {
    try {
      if (editedTemplate) {
        const response = await makePutRequest(`/v1/deal-estimating/template/${editedTemplate.id}`, {
          name,
          visibility,
          template: { ...template, items: fromItems(template.items) }
        })
        const array = [...templates]
        const index = templates.findIndex(u => u.id === editedTemplate.id)
        if (~index) {
          array.splice(index, 1, response)
          setTemplates(array)
        }
      } else {
        const response = await makePostRequest('/v1/deal-estimating/template', {
          name,
          visibility,
          template: { ...template, items: fromItems(template.items) }
        })
        setTemplates([...templates, response])
      }
      clearModal()
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: `Estimating template ${editedTemplate ? 'updated' : 'created'} successfuly`,
          level: 'success',
          autoDismiss: 20
        }
      })
    } catch (error) {
      clearModal()
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: `There was a problem ${editedTemplate ? 'updated' : 'created'} template`,
          level: 'error'
        }
      })
    }
  }

  return (
    <div>
      <AddButton onClick={() => setIsCreateModalVisible(true)}>Add New Estimate Template</AddButton>
      {templates.length > 0 && (
        <List
          templates={templates}
          deleteTemplate={deleteTemplate}
          openEditModal={openEditModal}
          cloneTemplate={cloneTemplate}
        />
      )}
      {isCreateModalVisible && (
        <CreateEstimatingTemplateModal
          editedTemplate={editedTemplate}
          isModalVisible={isCreateModalVisible}
          clearModal={clearModal}
          saveTemplate={saveTemplate}
        />
      )}
    </div>
  )
}

export default EstimateTemplates
